import React, { useMemo } from "react";
import AssigneesAvatarGroup, {
  type AssigneeAvatarItem,
} from "components/common/AssigneesAvatarGroup";
import type {
  EnquirySiteVisit,
  EnquirySiteVisitAssignmentSummary,
} from "features/EnquirySiteVisits/types/enquiry-site-visit.types";

function assigneeLabel(a: EnquirySiteVisitAssignmentSummary): string {
  const u = a.user;
  const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (u?.email) return u.email;
  const id = u?.id ?? a.toUserId;
  return id > 0 ? `User #${id}` : "—";
}

function assigneeUserId(a: EnquirySiteVisitAssignmentSummary): number | null {
  const raw = a.user?.id ?? a.toUserId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}


function dedupeKey(a: EnquirySiteVisitAssignmentSummary): number {
  const uid = assigneeUserId(a);
  if (uid != null) return uid;
  const id = Number(a.id);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

export default function SiteVisitAssigneesAvatarGroup({
  siteVisit,
  assignments,
  siteVisitId,
  canReadDetail,
  canAssignMore,
  onOpenAssign,
}: {
  siteVisit: EnquirySiteVisit;
  assignments?: EnquirySiteVisitAssignmentSummary[] | null;
  siteVisitId: number;
  canReadDetail: boolean;
  canAssignMore: boolean;
  onOpenAssign: (v: EnquirySiteVisit) => void;
}) {
  const assignees = useMemo<AssigneeAvatarItem[]>(() => {
    const list = assignments ?? [];
    const map = new Map<number, EnquirySiteVisitAssignmentSummary>();
    for (const a of list) {
      const k = dedupeKey(a);
      if (k > 0 && !map.has(k)) map.set(k, a);
    }
    return Array.from(map.values()).map((a) => ({
      key: dedupeKey(a) || a.id,
      userId: assigneeUserId(a),
      label: assigneeLabel(a),
      avatar: a.user?.avatar,
      employee: a.user?.employee,
    }));
  }, [assignments]);

  const detailTo = `/enquiry-management/site-visits/${siteVisitId}`;

  return (
    <AssigneesAvatarGroup
      assignees={assignees}
      detailTo={detailTo}
      canReadDetail={canReadDetail}
      canAssignMore={canAssignMore}
      onOpenAssign={() => onOpenAssign(siteVisit)}
      emptyTitle="No assignees — open site visit"
      addTitle="Assign user"
      showAddWhenLessThan={5}
    />
  );
}
